import { AfterViewInit, Component, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { TimeRange } from '../../../../../forms/fields/time-range/ziro-time-range-form.component';
import { ZiroFormsModule } from '../../../../../forms/ziro-forms.module';
import { TranslateModule } from '@ngx-translate/core';
import { ModalBodyClass } from '../../../../../modals/modal-body';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { DetailedModalComponent } from '../../../../../modals/detailed-modal/detailed-modal.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { Observable, of } from 'rxjs';
import {
  ZiroTimeRangesComponent,
  ZiroTimeRangesConfig,
} from '../../../../../forms/fields/time-range/ziro-time-ranges.component';
import { ButtonStyles } from '../../../../../button/button.component';
import { DateTimeSpan } from '../../../../../shared/models/generated/ziroModels';
import { FormsModule } from '@angular/forms';
import { ZiroRadioConfig } from '../../../../../forms/fields/radio/ziro-radio.component';

export interface WeeklyScheduleDayModalData {
  dayLabel: string;
  scheduleType: DayScheduleType;
  slots: TimeRange[];
  submitFn: (result: WeeklyScheduleDayModalResult) => Observable<WeeklyScheduleDayModalResult>;
}

export interface WeeklyScheduleDayModalResult {
  scheduleType: DayScheduleType;
  slots: TimeRange[];
}

export enum DayScheduleType {
  OPEN_24H = 'OPEN_24H',
  CUSTOM_HOURS = 'CUSTOM_HOURS',
  CLOSED = 'CLOSED',
}

export interface WeeklyScheduleDayFormData {
  scheduleType: DayScheduleType;
  slots: TimeRange[];
}

@Component({
  selector: 'ziro-weekly-schedule-day-modal',
  standalone: true,
  imports: [ZiroFormsModule, TranslateModule, ZiroTimeRangesComponent, FormsModule],
  templateUrl: './weekly-schedule-day-modal.component.html',
})
export class WeeklyScheduleDayModalComponent
  extends ModalBodyClass<WeeklyScheduleDayFormData>
  implements AfterViewInit, OnInit
{
  protected _ziroFormStateService = inject(ZiroFormStateService);
  private _modalComponent = inject(DetailedModalComponent<any>);

  @ViewChild(ZiroTimeRangesComponent) timeRangesForm: ZiroTimeRangesComponent;
  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  timeRanges = signal<DateTimeSpan[]>([{ start: '', end: '' }]);

  formConfig: ZiroFormConfig = {
    fields: {
      scheduleType: {
        label: () =>
          'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.schedule_type.label',
        dataAutomation: 'weekly-schedule-day-schedule-type',
        componentConfig: new ZiroRadioConfig<DayScheduleType>({
          buttons: [
            {
              value: DayScheduleType.OPEN_24H,
              label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.always_open',
            },
            {
              value: DayScheduleType.CUSTOM_HOURS,
              label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.custom_hours',
            },
            {
              value: DayScheduleType.CLOSED,
              label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.closed',
            },
          ],
          isDisplayVertical: true,
        }),
        required: () => true,
      },
      slots: {
        label: () =>
          'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.time_ranges.label',
        dataAutomation: 'weekly-schedule-day-slots',
        componentConfig: new ZiroTimeRangesConfig(),
        required: () => true,
        hidden: () => this.formData?.scheduleType !== DayScheduleType.CUSTOM_HOURS,
        valExcluded: () => this.formData?.scheduleType !== DayScheduleType.CUSTOM_HOURS,
      },
    },
  };
  childFormValidationState: ZiroFormsValidationState;
  validationStates = ZiroFormsValidationState;

  ngOnInit(): void {
    const data: WeeklyScheduleDayModalData = this._modalComponent.modalViewProperties.data;
    const initialScheduleType = data?.scheduleType ?? DayScheduleType.CUSTOM_HOURS;
    const initialSlots = data?.slots?.length ? data.slots : [{ start: '09:00', end: '17:00' }];
    this.entitySource.next({ scheduleType: initialScheduleType, slots: initialSlots });
    this.timeRanges.set(data?.slots ?? [{ start: '', end: '' }]);

    this._validateAndSubmitSource.subscribe((submitted) => {
      if (submitted) {
        this.isFormSubmitted = true;
      }
    });
    let initialized = false;
    this.ziroFormsUpdate$.subscribe((update) => {
      const prev = (update.old as WeeklyScheduleDayFormData)?.scheduleType;
      const next = (update.new as WeeklyScheduleDayFormData)?.scheduleType;

      if (!initialized) {
        initialized = true;
        this.setDisplayFormValidationState();
        return;
      }

      if (prev === next) {
        this.setDisplayFormValidationState();
        return;
      }

      if (next === DayScheduleType.OPEN_24H) {
        const allDaySlot: TimeRange = { start: '00:00', end: '23:59' };
        this.entitySource.next({ scheduleType: next, slots: [allDaySlot] });
      } else if (next === DayScheduleType.CLOSED) {
        this.entitySource.next({ scheduleType: next, slots: [] });
      } else {
        const defaultSlot: TimeRange = { start: '', end: '' };
        this.entitySource.next({ scheduleType: next, slots: [defaultSlot] });
      }
    });

    this.timeRangesForm?.childFormComponent.ziroFormsUpdate$.subscribe((update) => {
      this.onRangesUpdate(update);
    });
  }

  onRangesUpdate(update: ZiroFormsUpdate<TimeRange[]>): void {
    this.timeRanges.set(update.new ?? []);
    this.setDisplayFormValidationState();
  }

  setDisplayFormValidationState(): void {
    this.childFormValidationState = this.timeRangesForm.showValidation
      ? this.timeRangesForm.validationState
      : this.validationStates.VALID;
  }

  submit(): Observable<WeeklyScheduleDayModalResult> {
    this.setDisplayFormValidationState();

    if (!this.isFormValid()) {
      return of(null);
    }
    const result: WeeklyScheduleDayModalResult = {
      scheduleType: this.formData.scheduleType,
      slots: this.formData.slots ?? [],
    };
    return this._modalComponent.modalViewProperties.data.submitFn(result);
  }

  protected readonly ButtonStyles = ButtonStyles;
}
